import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "@/App";
import { usePolling } from "@/hooks/usePolling";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Storefront, CurrencyInr, Receipt, Percent, PencilSimple, Plus, ArrowRight, Clock, Camera, Globe, MapPin, Phone, ArrowLeft, QrCode, MagnifyingGlass, UserCircle, CheckCircle, User, Users, ShieldCheck } from "@phosphor-icons/react";
import { LoadingImage } from "@/components/LoadingImage";
import QRScanner from "@/components/QRScanner";

export default function PartnerStoreDashboard() {
  const { user, token, logout } = useAuth();
  const [store, setStore] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTxnDialog, setShowTxnDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDiscountDialog, setShowDiscountDialog] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [memberSearchQuery, setMemberSearchQuery] = useState("");
  const [memberSearchResults, setMemberSearchResults] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [txnForm, setTxnForm] = useState({ member_name: "", member_phone: "", total_bill: "", discount_type: "percentage", discount_value: "" });
  const [editForm, setEditForm] = useState({});
  const [discountForm, setDiscountForm] = useState({ discount_percentage: "", discount_fixed: "" });
  const [uploading, setUploading] = useState(null);

  const handleUploadImage = async (file, type) => {
    if (!file) return;
    setUploading(type);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload/image`, formData, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } });
      const url = res.data.url;
      await axios.put(`${API}/partner-stores/${store.id}`, { [type === "logo" ? "logo_url" : "cover_url"]: url }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${type === "logo" ? "Logo" : "Cover photo"} updated!`);
      fetchStore();
    } catch (e) { toast.error("Upload failed"); }
    finally { setUploading(null); }
  };

  const fetchStore = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/partner-stores/my-store`, { headers: { Authorization: `Bearer ${token}` } });
      setStore(res.data);
      if (res.data) {
        setEditForm({ store_name: res.data.store_name, store_description: res.data.store_description, store_address: res.data.store_address, store_phone: res.data.store_phone, store_category: res.data.store_category, google_maps_link: res.data.google_maps_link || "", website: res.data.website || "" });
        setDiscountForm({ discount_percentage: res.data.discount_percentage || "", discount_fixed: res.data.discount_fixed || "" });
      }
    } catch {} finally { setLoading(false); }
  }, [token]);

  const fetchTransactions = useCallback(async () => {
    if (!store?.id) return;
    try {
      const res = await axios.get(`${API}/partner-stores/${store.id}/transactions`, { headers: { Authorization: `Bearer ${token}` } });
      setTransactions(res.data);
    } catch {}
  }, [store?.id, token]);

  useEffect(() => { fetchStore(); }, [fetchStore]);
  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);
  usePolling(fetchStore, 10000);
  usePolling(fetchTransactions, 10000);

  const calculatedDiscount = (() => {
    const bill = parseFloat(txnForm.total_bill) || 0;
    const val = parseFloat(txnForm.discount_value) || 0;
    if (txnForm.discount_type === "percentage") return Math.round(bill * val / 100 * 100) / 100;
    return Math.min(val, bill);
  })();

  const handleCreateTransaction = async () => {
    if (!txnForm.member_name || !txnForm.member_phone || !txnForm.total_bill || !txnForm.discount_value) {
      toast.error("Please fill all fields"); return;
    }
    try {
      await axios.post(`${API}/partner-stores/${store.id}/transactions`, {
        ...txnForm, total_bill: parseFloat(txnForm.total_bill), discount_value: parseFloat(txnForm.discount_value),
        member_id: selectedMember?.id || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Transaction recorded!");
      setShowTxnDialog(false);
      setTxnForm({ member_name: "", member_phone: "", total_bill: "", discount_type: "percentage", discount_value: "" });
      setSelectedMember(null);
      setMemberSearchQuery("");
      setMemberSearchResults([]);
      fetchStore(); fetchTransactions();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to record transaction"); }
  };

  const handleQrScan = async (data) => {
    setShowScanner(false);
    try {
      const res = await axios.get(`${API}/member-lookup?q=${encodeURIComponent(data)}`);
      if (res.data.found && res.data.member) {
        const m = res.data.member;
        setSelectedMember(m);
        setTxnForm(prev => ({ ...prev, member_name: m.full_name, member_phone: m.phone?.replace(/\D/g, "").slice(-10) || "" }));
        toast.success(`Member found: ${m.full_name}`);
      } else {
        toast.error("No BCH member found for this QR code");
      }
    } catch { toast.error("Failed to lookup member"); }
  };

  const handleMemberSearch = async (query) => {
    setMemberSearchQuery(query);
    if (query.length < 2) { setMemberSearchResults([]); return; }
    try {
      const res = await axios.get(`${API}/member-lookup?q=${encodeURIComponent(query)}`);
      if (res.data.found) {
        setMemberSearchResults(res.data.members || (res.data.member ? [res.data.member] : []));
      } else { setMemberSearchResults([]); }
    } catch { setMemberSearchResults([]); }
  };

  const selectMember = (m) => {
    setSelectedMember(m);
    setTxnForm(prev => ({ ...prev, member_name: m.full_name, member_phone: m.phone?.replace(/\D/g, "").slice(-10) || "" }));
    setMemberSearchResults([]);
    setMemberSearchQuery("");
  };

  const handleUpdateStore = async () => {
    try {
      await axios.put(`${API}/partner-stores/${store.id}`, editForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Store updated!");
      setShowEditDialog(false);
      fetchStore();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update store"); }
  };

  const handleRequestDiscountChange = async () => {
    try {
      await axios.post(`${API}/partner-stores/${store.id}/request-discount-change`, {
        discount_percentage: discountForm.discount_percentage ? parseFloat(discountForm.discount_percentage) : null,
        discount_fixed: discountForm.discount_fixed ? parseFloat(discountForm.discount_fixed) : null
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Discount change request submitted for admin approval");
      setShowDiscountDialog(false);
      fetchStore();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to request discount change"); }
  };

  if (loading) return <div className="min-h-screen bg-main flex items-center justify-center"><div className="w-8 h-8 border-2 border-bch-teal border-t-transparent rounded-full animate-spin" /></div>;

  if (!store) return (
    <div className="min-h-screen bg-main flex items-center justify-center p-4">
      <Card className="max-w-md w-full bg-card border-[var(--border-color)]">
        <CardContent className="p-8 text-center">
          <Storefront className="w-16 h-16 text-bch-gold mx-auto mb-4" weight="duotone" />
          <h2 className="text-primary text-xl font-bold mb-2">No Partner Store Found</h2>
          <p className="text-muted text-sm mb-4">Your store application is being processed or not yet submitted.</p>
          <div className="flex gap-3 justify-center">
            {user?.role !== "partner_store" && <a href="/stores"><Button variant="outline" className="border-[var(--border-color)] text-muted"><ArrowLeft className="w-4 h-4 mr-1" /> Back to BCH</Button></a>}
            <Button onClick={logout} variant="outline" className="border-[var(--border-color)] text-muted">Sign Out</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  if (store.status === "pending") return (
    <div className="min-h-screen bg-main flex items-center justify-center p-4">
      <Card className="max-w-md w-full bg-card border-[var(--border-color)]">
        <CardContent className="p-8 text-center">
          <Clock className="w-16 h-16 text-amber-500 mx-auto mb-4" weight="duotone" />
          <h2 className="text-primary text-xl font-bold mb-2">Application Pending</h2>
          <p className="text-muted text-sm mb-4">Your partner store application for <strong className="text-primary">{store.store_name}</strong> is awaiting admin approval.</p>
          <div className="flex gap-3 justify-center">
            {user?.role !== "partner_store" && <a href="/stores"><Button variant="outline" className="border-[var(--border-color)] text-muted"><ArrowLeft className="w-4 h-4 mr-1" /> Back to BCH</Button></a>}
            <Button onClick={logout} variant="outline" className="border-[var(--border-color)] text-muted">Sign Out</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-main">
      {/* Cover Photo with Header */}
      <div className="relative h-48 bg-gradient-to-br from-bch-gold/20 via-bch-teal/10 to-bch-gold/5 overflow-hidden">
        {store.cover_url && <img src={store.cover_url} alt="cover" className="w-full h-full object-cover" />}
        <label className="absolute top-3 right-3 cursor-pointer px-3 py-1.5 bg-black/50 text-white text-xs rounded-lg flex items-center gap-1.5 hover:bg-black/70 transition-colors" data-testid="upload-cover-btn">
          <Camera className="w-4 h-4" /> {uploading === "cover" ? "Uploading..." : "Cover Photo"}
          <input type="file" accept="image/*" className="hidden" onChange={e => handleUploadImage(e.target.files[0], "cover")} disabled={!!uploading} />
        </label>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[var(--bg-main)] to-transparent h-20" />
      </div>
      <div className="max-w-6xl mx-auto px-6 -mt-10 relative z-10">
        <div className="flex items-end gap-4 mb-6">
          <div className="relative">
            {store.logo_url ? (
              <img src={store.logo_url} alt="logo" className="w-20 h-20 rounded-2xl bg-white object-cover shadow-lg border-4 border-[var(--bg-main)]" />
            ) : (
              <div className="w-20 h-20 rounded-2xl bg-card border-4 border-[var(--bg-main)] shadow-lg flex items-center justify-center">
                <Storefront className="w-8 h-8 text-bch-gold" weight="duotone" />
              </div>
            )}
            <label className="absolute -bottom-1 -right-1 w-7 h-7 bg-bch-teal text-white rounded-full flex items-center justify-center cursor-pointer shadow-lg hover:bg-bch-teal/80 transition-colors" data-testid="upload-logo-btn">
              <Camera className="w-3.5 h-3.5" />
              <input type="file" accept="image/*" className="hidden" onChange={e => handleUploadImage(e.target.files[0], "logo")} disabled={!!uploading} />
            </label>
          </div>
          <div className="flex-1">
            <h1 className="text-primary font-bold text-xl" data-testid="partner-store-name">{store.store_name}</h1>
            <p className="text-muted text-xs">Partner Store Dashboard</p>
          </div>
          <div className="flex items-center gap-3">
            {user?.role !== "partner_store" && (
              <a href="/stores">
                <Button variant="outline" size="sm" className="border-[var(--border-color)] text-muted" data-testid="back-to-stores-btn">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back to BCH
                </Button>
              </a>
            )}
            <span className="text-muted text-sm hidden sm:inline">{user?.full_name}</span>
            <Button onClick={logout} variant="outline" size="sm" className="border-[var(--border-color)] text-muted" data-testid="partner-logout-btn">Sign Out</Button>
          </div>
        </div>
        {/* Stats Cards */}
        <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="bg-card border-[var(--border-color)]">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-bch-teal/10 flex items-center justify-center"><Receipt className="w-6 h-6 text-bch-teal" weight="duotone" /></div>
              <div><p className="text-muted text-xs uppercase tracking-wider">Transactions</p><p className="text-primary text-2xl font-bold" data-testid="stat-transactions">{store.total_transactions}</p></div>
            </CardContent>
          </Card>
          <Card className="bg-card border-[var(--border-color)]">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-rose-500/10 flex items-center justify-center"><Percent className="w-6 h-6 text-rose-500" weight="duotone" /></div>
              <div><p className="text-muted text-xs uppercase tracking-wider">Total Discount Given</p><p className="text-primary text-2xl font-bold" data-testid="stat-discount">{store.total_discount_given?.toFixed(2)}</p></div>
            </CardContent>
          </Card>
          <Card className="bg-card border-[var(--border-color)]">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-bch-gold/10 flex items-center justify-center"><CurrencyInr className="w-6 h-6 text-bch-gold" weight="duotone" /></div>
              <div><p className="text-muted text-xs uppercase tracking-wider">Total Revenue</p><p className="text-primary text-2xl font-bold" data-testid="stat-revenue">{store.total_revenue?.toFixed(2)}</p></div>
            </CardContent>
          </Card>
        </div>

        {/* Store Info + Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-1 bg-card border-[var(--border-color)]">
            <CardHeader className="pb-3"><CardTitle className="text-primary text-base">Store Details</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div><p className="text-muted text-xs">Category</p><p className="text-primary font-medium">{store.store_category}</p></div>
              <div><p className="text-muted text-xs">Address</p><p className="text-primary">{store.store_address}</p></div>
              <div><p className="text-muted text-xs">Phone</p><p className="text-primary">{store.store_phone}</p></div>
              <div><p className="text-muted text-xs">Description</p><p className="text-primary">{store.store_description}</p></div>
              {store.store_phone && <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-muted" weight="bold" /><a href={`tel:${store.store_phone}`} className="text-primary text-sm hover:text-bch-teal">{store.store_phone}</a></div>}
              {store.website && <div className="flex items-center gap-2"><Globe className="w-4 h-4 text-muted" weight="bold" /><a href={store.website?.startsWith("http") ? store.website : `https://${store.website}`} target="_blank" rel="noopener noreferrer" className="text-blue-400 text-sm hover:underline truncate">{store.website}</a></div>}
              {store.google_maps_link ? (
                <a href={store.google_maps_link} target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full px-3 py-2 bg-blue-500/10 text-blue-400 rounded-lg text-sm font-semibold hover:bg-blue-500/20 transition-colors" data-testid="directions-link">
                  <MapPin className="w-4 h-4" weight="bold" /> Get Directions
                </a>
              ) : (
                <button onClick={() => setShowEditDialog(true)}
                  className="flex items-center justify-center gap-2 w-full px-3 py-2 border border-dashed border-[var(--border-color)] text-muted rounded-lg text-sm hover:border-blue-400 hover:text-blue-400 transition-colors" data-testid="add-directions-btn">
                  <MapPin className="w-4 h-4" weight="bold" /> Add Google Maps Link
                </button>
              )}
              <div className="border-t border-[var(--border-color)] pt-3">
                <p className="text-muted text-xs mb-1">Current Discounts</p>
                {store.discount_percentage && <p className="text-bch-teal font-medium">{store.discount_percentage}% off</p>}
                {store.discount_fixed && <p className="text-bch-gold font-medium">Flat {store.discount_fixed} off</p>}
                {store.discount_change_status === "pending" && <p className="text-amber-500 text-xs mt-1">Discount change pending approval</p>}
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={() => setShowEditDialog(true)} variant="outline" size="sm" className="flex-1 border-[var(--border-color)] text-muted" data-testid="edit-store-btn">
                  <PencilSimple className="w-4 h-4 mr-1" /> Edit
                </Button>
                <Button onClick={() => setShowDiscountDialog(true)} variant="outline" size="sm" className="flex-1 border-[var(--border-color)] text-muted" data-testid="change-discount-btn">
                  <Percent className="w-4 h-4 mr-1" /> Discount
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Transactions */}
          <Card className="lg:col-span-2 bg-card border-[var(--border-color)]">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-primary text-base">Recent Transactions</CardTitle>
              <Dialog open={showTxnDialog} onOpenChange={setShowTxnDialog}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-bch-teal hover:bg-bch-teal/90 text-white" data-testid="new-transaction-btn">
                    <Plus className="w-4 h-4 mr-1" /> New Transaction
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-card border-[var(--border-color)]">
                  <DialogHeader><DialogTitle className="text-primary">Record Transaction</DialogTitle></DialogHeader>
                  <div className="space-y-4 mt-2">
                    {/* Member Identification */}
                    {selectedMember ? (
                      <div className="space-y-3" data-testid="selected-member-info">
                        {/* Identity Verification Header */}
                        <div className="flex items-center gap-2 px-1">
                          <ShieldCheck className="w-4 h-4 text-bch-teal" weight="fill" />
                          <span className="text-bch-teal text-xs font-semibold uppercase tracking-wider">Identity Verification</span>
                          <div className="flex-1" />
                          <Button variant="ghost" size="sm" className="text-muted h-7 text-xs" onClick={() => { setSelectedMember(null); setTxnForm(prev => ({...prev, member_name: "", member_phone: ""})); }} data-testid="clear-member-btn">Change</Button>
                        </div>
                        {/* Member Card with Photo */}
                        <div className="flex items-center gap-4 p-4 bg-bch-teal/5 border border-bch-teal/20 rounded-xl">
                          <div className="w-16 h-16 rounded-xl overflow-hidden border-2 border-bch-gold/30 shrink-0 bg-surface" data-testid="member-profile-photo">
                            {selectedMember.profile_picture ? (
                              <LoadingImage src={selectedMember.profile_picture} className="w-16 h-16 rounded-xl" fallback={<div className="w-full h-full flex items-center justify-center"><User className="w-8 h-8 text-muted" weight="fill" /></div>} />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-bch-teal/10"><User className="w-8 h-8 text-bch-teal/40" weight="fill" /></div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-500 shrink-0" weight="fill" />
                              <p className="text-primary font-semibold text-sm truncate">{selectedMember.full_name}</p>
                            </div>
                            <p className="text-muted text-xs mt-0.5">{selectedMember.bch_id} &middot; {selectedMember.business_name}</p>
                          </div>
                        </div>
                        {/* Family Members for Identity Verification */}
                        {selectedMember.family_members?.length > 0 && (
                          <div className="p-3 bg-pink-500/5 border border-pink-500/15 rounded-xl" data-testid="family-verification-section">
                            <div className="flex items-center gap-2 mb-2.5">
                              <Users className="w-3.5 h-3.5 text-pink-500" weight="fill" />
                              <span className="text-pink-500 text-[10px] font-semibold uppercase tracking-wider">Authorized Family ({selectedMember.family_members.length})</span>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              {selectedMember.family_members.map((fm, idx) => (
                                <div key={fm.id || idx} className="flex items-center gap-2.5 p-2 bg-card rounded-lg border border-[var(--border-color)]" data-testid={`family-verify-${idx}`}>
                                  <div className="w-10 h-10 rounded-lg overflow-hidden shrink-0 border border-pink-500/20">
                                    {fm.photo_url ? (
                                      <LoadingImage src={fm.photo_url} className="w-10 h-10 rounded-lg" fallback={<div className="w-full h-full bg-pink-500/10 flex items-center justify-center"><User className="w-5 h-5 text-pink-500/40" weight="fill" /></div>} />
                                    ) : (
                                      <div className="w-full h-full bg-pink-500/10 flex items-center justify-center"><User className="w-5 h-5 text-pink-500/40" weight="fill" /></div>
                                    )}
                                  </div>
                                  <div className="min-w-0">
                                    <p className="text-primary text-xs font-medium truncate">{fm.name}</p>
                                    <p className="text-pink-500 text-[10px]">{fm.relation}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex gap-2">
                          <Button onClick={() => setShowScanner(true)} variant="outline" className="flex-1 border-[var(--border-color)] text-primary" data-testid="scan-qr-btn">
                            <QrCode className="w-5 h-5 mr-2 text-bch-gold" weight="duotone" /> Scan BCH ID
                          </Button>
                          <div className="flex-1 relative">
                            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" weight="bold" />
                            <Input
                              placeholder="Search name, phone, or BCH ID..."
                              value={memberSearchQuery}
                              onChange={e => handleMemberSearch(e.target.value)}
                              className="pl-9 bg-surface border-[var(--border-color)] text-primary"
                              data-testid="member-search-input"
                            />
                          </div>
                        </div>
                        {memberSearchResults.length > 0 && (
                          <div className="bg-surface border border-[var(--border-color)] rounded-xl overflow-hidden max-h-40 overflow-y-auto" data-testid="member-search-results">
                            {memberSearchResults.map(m => (
                              <button key={m.id} onClick={() => selectMember(m)} className="w-full flex items-center gap-3 p-3 hover:bg-bch-teal/5 transition-colors text-left border-b border-[var(--border-color)] last:border-b-0">
                                <UserCircle className="w-8 h-8 text-bch-gold" weight="duotone" />
                                <div>
                                  <p className="text-primary text-sm font-medium">{m.full_name}</p>
                                  <p className="text-muted text-xs">{m.bch_id} &middot; {m.phone}</p>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                        <p className="text-muted text-[10px] text-center uppercase tracking-wider">or enter manually</p>
                      </div>
                    )}

                    <Input placeholder="Member Name" value={txnForm.member_name} onChange={e => setTxnForm({...txnForm, member_name: e.target.value})} className="bg-surface border-[var(--border-color)] text-primary" data-testid="txn-member-name" />
                    <Input placeholder="Member Phone (10 digits)" value={txnForm.member_phone} onChange={e => { const v = e.target.value.replace(/\D/g, "").slice(0, 10); setTxnForm({...txnForm, member_phone: v}); }} className="bg-surface border-[var(--border-color)] text-primary" data-testid="txn-member-phone" />
                    <Input type="number" placeholder="Total Bill Amount" value={txnForm.total_bill} onChange={e => setTxnForm({...txnForm, total_bill: e.target.value})} className="bg-surface border-[var(--border-color)] text-primary" data-testid="txn-total-bill" />
                    <Select value={txnForm.discount_type} onValueChange={v => setTxnForm({...txnForm, discount_type: v})}>
                      <SelectTrigger className="bg-surface border-[var(--border-color)] text-primary" data-testid="txn-discount-type"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-card border-[var(--border-color)]">
                        <SelectItem value="percentage">Percentage (%)</SelectItem>
                        <SelectItem value="fixed">Fixed Amount</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input type="number" placeholder={txnForm.discount_type === "percentage" ? "Discount %" : "Discount Amount"} value={txnForm.discount_value} onChange={e => setTxnForm({...txnForm, discount_value: e.target.value})} className="bg-surface border-[var(--border-color)] text-primary" data-testid="txn-discount-value" />
                    
                    {txnForm.total_bill && txnForm.discount_value && (
                      <div className="bg-bch-teal/5 border border-bch-teal/20 rounded-xl p-4 space-y-2" data-testid="txn-calculation">
                        <div className="flex justify-between text-sm"><span className="text-muted">Total Bill</span><span className="text-primary font-medium">{parseFloat(txnForm.total_bill).toFixed(2)}</span></div>
                        <div className="flex justify-between text-sm"><span className="text-muted">Discount</span><span className="text-rose-500 font-medium">- {calculatedDiscount.toFixed(2)}</span></div>
                        <div className="border-t border-[var(--border-color)] pt-2 flex justify-between"><span className="text-primary font-semibold">Discounted Amount</span><span className="text-bch-teal font-bold text-lg">{(parseFloat(txnForm.total_bill) - calculatedDiscount).toFixed(2)}</span></div>
                      </div>
                    )}
                    
                    <Button onClick={handleCreateTransaction} className="w-full bg-bch-teal hover:bg-bch-teal/90 text-white" data-testid="txn-submit-btn">
                      <Receipt className="w-4 h-4 mr-2" /> Record Transaction
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                {transactions.length === 0 ? (
                  <div className="text-center py-12"><Receipt className="w-12 h-12 text-muted mx-auto mb-3" weight="duotone" /><p className="text-muted text-sm">No transactions yet</p></div>
                ) : (
                  <div className="space-y-3">
                    {transactions.map(txn => (
                      <div key={txn.id} className="p-4 bg-surface rounded-xl border border-[var(--border-color)] flex items-center justify-between" data-testid={`txn-${txn.id}`}>
                        <div>
                          <p className="text-primary font-medium text-sm">{txn.member_name}</p>
                          <p className="text-muted text-xs">{txn.member_phone} &middot; {new Date(txn.created_at).toLocaleDateString()}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-primary font-bold">{txn.discounted_total.toFixed(2)}</p>
                          <p className="text-rose-500 text-xs">-{txn.discount_amount.toFixed(2)} ({txn.discount_type === "percentage" ? `${txn.discount_value}%` : "flat"})</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
      </div>

      {/* Edit Store Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-card border-[var(--border-color)]">
          <DialogHeader><DialogTitle className="text-primary">Edit Store Details</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <Input placeholder="Store Name" value={editForm.store_name || ""} onChange={e => setEditForm({...editForm, store_name: e.target.value})} className="bg-surface border-[var(--border-color)] text-primary" data-testid="edit-store-name" />
            <Input placeholder="Description" value={editForm.store_description || ""} onChange={e => setEditForm({...editForm, store_description: e.target.value})} className="bg-surface border-[var(--border-color)] text-primary" />
            <Input placeholder="Address" value={editForm.store_address || ""} onChange={e => setEditForm({...editForm, store_address: e.target.value})} className="bg-surface border-[var(--border-color)] text-primary" />
            <Input placeholder="Phone" value={editForm.store_phone || ""} onChange={e => setEditForm({...editForm, store_phone: e.target.value})} className="bg-surface border-[var(--border-color)] text-primary" />
            <Input placeholder="Category" value={editForm.store_category || ""} onChange={e => setEditForm({...editForm, store_category: e.target.value})} className="bg-surface border-[var(--border-color)] text-primary" />
            <Input placeholder="Google Maps Link" value={editForm.google_maps_link || ""} onChange={e => setEditForm({...editForm, google_maps_link: e.target.value})} className="bg-surface border-[var(--border-color)] text-primary" data-testid="edit-maps-link" />
            <Input placeholder="Website URL" value={editForm.website || ""} onChange={e => setEditForm({...editForm, website: e.target.value})} className="bg-surface border-[var(--border-color)] text-primary" data-testid="edit-website" />
            <Button onClick={handleUpdateStore} className="w-full bg-bch-teal hover:bg-bch-teal/90 text-white" data-testid="save-store-btn">Save Changes</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Discount Change Dialog */}
      <Dialog open={showDiscountDialog} onOpenChange={setShowDiscountDialog}>
        <DialogContent className="bg-card border-[var(--border-color)]">
          <DialogHeader><DialogTitle className="text-primary">Request Discount Change</DialogTitle></DialogHeader>
          <p className="text-muted text-sm">Changes to discount require Super Admin approval.</p>
          <div className="space-y-4 mt-2">
            <Input type="number" placeholder="Discount Percentage (%)" value={discountForm.discount_percentage} onChange={e => setDiscountForm({...discountForm, discount_percentage: e.target.value})} className="bg-surface border-[var(--border-color)] text-primary" data-testid="discount-pct-input" />
            <Input type="number" placeholder="Fixed Discount Amount" value={discountForm.discount_fixed} onChange={e => setDiscountForm({...discountForm, discount_fixed: e.target.value})} className="bg-surface border-[var(--border-color)] text-primary" data-testid="discount-fixed-input" />
            <Button onClick={handleRequestDiscountChange} className="w-full bg-bch-gold hover:bg-bch-gold/90 text-black" data-testid="submit-discount-btn">Submit for Approval</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Scanner Dialog */}
      <Dialog open={showScanner} onOpenChange={setShowScanner}>
        <DialogContent className="bg-card border-[var(--border-color)]">
          <DialogHeader><DialogTitle className="text-primary">Scan BCH Member ID</DialogTitle></DialogHeader>
          <QRScanner onScan={handleQrScan} onClose={() => setShowScanner(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
